import React, { useEffect, useRef, useState } from 'react';

const MAX_SECONDS = 120;
const MAX_BYTES = 12 * 1024 * 1024;
const types = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/webm', 'audio/ogg;codecs=opus'];
const extension = mime => mime.includes('mp4') ? 'm4a' : mime.includes('ogg') ? 'ogg' : 'webm';

export default function VoiceRecorder({ onSend, disabled = false }) {
  const [stage, setStage] = useState('idle');
  const [seconds, setSeconds] = useState(0);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState('');
  const recorder = useRef(null);
  const stream = useRef(null);
  const timer = useRef(null);
  const chunks = useRef([]);
  const cancelled = useRef(false);
  const latestDraft = useRef(null);
  const mounted = useRef(true);

  const cleanMicrophone = () => {
    if (timer.current) clearInterval(timer.current);
    timer.current = null;
    stream.current?.getTracks().forEach(track => track.stop());
    stream.current = null;
  };
  const dropDraft = () => {
    if (latestDraft.current?.url) URL.revokeObjectURL(latestDraft.current.url);
    latestDraft.current = null;
    setDraft(null);
  };
  const stop = () => {
    if (!recorder.current || recorder.current.state !== 'recording') return;
    setStage('processing');
    recorder.current.stop();
    cleanMicrophone();
  };
  const discard = () => {
    cancelled.current = true;
    if (recorder.current?.state === 'recording') recorder.current.stop();
    recorder.current = null;
    cleanMicrophone(); dropDraft(); setSeconds(0); setStage('idle'); setError('');
  };
  const start = async () => {
    if (disabled) return;
    discard();
    cancelled.current = false;
    setStage('requesting');
    try {
      if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
        throw new Error('Microphone recording is unavailable. Use HTTPS and a supported browser.');
      }
      const microphone = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (cancelled.current || !mounted.current) {
        microphone.getTracks().forEach(track => track.stop());
        return;
      }
      stream.current = microphone;
      const chosen = types.find(type => MediaRecorder.isTypeSupported(type));
      const mediaRecorder = new MediaRecorder(microphone, chosen ? { mimeType: chosen } : undefined);
      const mime = mediaRecorder.mimeType || chosen || 'audio/webm';
      chunks.current = [];
      recorder.current = mediaRecorder;
      const startedAt = Date.now();
      mediaRecorder.ondataavailable = e => { if (e.data?.size) chunks.current.push(e.data); };
      mediaRecorder.onerror = () => { cleanMicrophone(); setError('Recording failed; please try again.'); setStage('idle'); };
      mediaRecorder.onstop = () => {
        cleanMicrophone();
        if (cancelled.current || !mounted.current || recorder.current !== mediaRecorder) return;
        const blob = new Blob(chunks.current, { type: mime });
        if (!blob.size) { setError('No audio recorded. Please retry.'); setStage('idle'); return; }
        if (blob.size > MAX_BYTES) { setError('Voice note exceeds 12 MB. Please record a shorter note.'); setStage('idle'); return; }
        const file = new File([blob], `support-voice-${Date.now()}.${extension(mime)}`, { type: mime });
        const nextDraft = { file, url: URL.createObjectURL(blob), durationMs: Math.min(MAX_SECONDS * 1000, Date.now() - startedAt) };
        latestDraft.current = nextDraft;
        setDraft(nextDraft);
        setStage('preview');
      };
      mediaRecorder.start(250);
      setStage('recording');
      timer.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startedAt) / 1000);
        setSeconds(Math.min(MAX_SECONDS, elapsed));
        if (elapsed >= MAX_SECONDS && mediaRecorder.state === 'recording') stop();
      }, 200);
    } catch (err) { cleanMicrophone(); setError(err.message || 'Could not access microphone'); setStage('idle'); }
  };
  const send = async () => {
    if (!draft || disabled || stage === 'sending') return;
    setStage('sending'); setError('');
    try {
      await onSend(draft.file, draft.durationMs);
      discard();
    } catch (err) { setError(err.message || 'Upload failed. Draft kept.'); setStage('preview'); }
  };
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      cancelled.current = true;
      if (recorder.current?.state === 'recording') recorder.current.stop();
      cleanMicrophone();
      if (latestDraft.current?.url) URL.revokeObjectURL(latestDraft.current.url);
    };
  }, []);

  return <div className="gp-voice">
    {stage === 'idle' && <button type="button" className="gp-icon-btn" disabled={disabled} onClick={() => void start()} title="Record voice note">🎙️</button>}
    {(stage === 'requesting' || stage === 'recording' || stage === 'processing') &&
      <div className="gp-record-row"><span className="gp-rec-light" />
        <b>{String(Math.floor(seconds / 60)).padStart(2, '0')}:{String(seconds % 60).padStart(2, '0')}</b>
        <span>{stage === 'requesting' ? 'Allow microphone…' : stage === 'processing' ? 'Preparing preview…' : 'Recording (not sent)'}</span>
        <button type="button" className="gp-mini gp-danger" onClick={discard}>Delete</button>
        <button type="button" className="gp-mini gp-primary" disabled={stage !== 'recording'} onClick={stop}>■ Stop</button>
      </div>}
    {(stage === 'preview' || stage === 'sending') && draft &&
      <div className="gp-preview"><div className="gp-preview-title">🎧 Listen before sending — not sent yet</div>
        <audio key={draft.url} controls src={draft.url} preload="metadata" />
        <div className="gp-preview-actions">
          <button type="button" className="gp-mini gp-danger" disabled={stage === 'sending'} onClick={discard}>🗑 Delete</button>
          <button type="button" className="gp-mini" disabled={stage === 'sending'} onClick={() => void start()}>↻ Re-record</button>
          <button type="button" className="gp-mini gp-primary" disabled={stage === 'sending' || disabled} onClick={() => void send()}>{stage === 'sending' ? 'Sending…' : '➤ Send voice'}</button>
        </div>
      </div>}
    {error && <p className="gp-error gp-voice-error">{error}</p>}
  </div>;
}
